import { safeJsonStringify } from '@direct-frontend/stdlib';
import { UnexpectedError } from '@adv-frontend/error';
import { ReactionMonitoring } from './reactionMonitoring';
import { type DataGenerator, type EffectFn, ReactionInstance } from './ReactionInstance';
import { InstanceCache } from './instanceCache';
import { tickScheduler } from './tickScheduler';
import { immediateScheduler } from './immediateScheduler';

export const $INTERNALS = Symbol('reaction internals symbol');

export type Detach = () => void;

export type Internals<R, P> = {
    getUnsubscribes: () => Set<() => void>;
    getInstances: () => Map<unknown, ReactionInstance<R, P>>;
    getOrphanedInstances: () => Set<ReactionInstance<R, P>>;
};

type StartReturnValue<R, P> = Detach & {
    getInstance: () => ReactionInstance<R, P> | null;
    [$INTERNALS]: Internals<R, P>;
};

type Start<R, P> = {
    (): StartReturnValue<R, never>;
    (param: P): StartReturnValue<R, P>;
};

export type Reaction<R, P = never> = {
    start: Start<R, P>;
    [$INTERNALS]: {
        definition: ReactionDefinition<R, P>;
        getInstances: () => Map<P, ReactionInstance<R, P>>;
    };
};

/**
 * Определение реакции
 */
export type ReactionDefinition<R, P> = {
    /**
     * Функция-генератор, агрегирующая источники данных для последующего запуска эффекта.
     * На используемые источники создаются подписки.
     *
     * @param param - параметр реакции
     * @param reactionApi - интерфейс взаимодействия с реакцией изнутри реакции
     * @returns result - значение, передаваемое в эффект
     */
    data: DataGenerator<R, P>;

    /**
     * Действие, выполняемое в результате изменений источников в data.
     *
     * @param result - значение, переданное из *data
     * @param reactionApi - интерфейс взаимодействия с реакцией изнутри реакции
     * @returns void
     */
    effect: EffectFn<R, P>;

    /**
     * Конфигурация батчинга запусков экземпляра реакции.
     * При передаче false - батчинг отключен
     */
    autoBatch?: 'tick' | false;
};

/**
 * Функция для создания реакции
 *
 * @link узнать подробнее: https://a.yandex-team.ru/arcadia/adv/frontend/docs/modules/pages/rfc/reactions.md#faq
 *
 * @param definition - Декларация реакции
 * @returns Инстанс реакции
 */
export function createReaction<R, P = never>(definition: ReactionDefinition<R, P>): Reaction<R, P> {
    // Ошибка сохраняющая stacktrace для идентификации реакции
    const reactionError = new Error();

    // Рантайм мониторинг выполнения реакций
    const reactionMonitoring = new ReactionMonitoring(reactionError);

    // Кеш экземпляров с дедупликацией по параметрам
    const cache = new InstanceCache<R, P>();

    // Экземпляры, у которых не осталось потребителей, но остались незавершенные запуски.
    // Из кеша они уже удалены, поэтому иначе `cancelAnyParamsActiveRuns` до них не добирается.
    const orphanedInstances = new Set<ReactionInstance<R, P>>();

    // Выбираем планировщик в зависимости от настройки autoBatch
    const scheduler = definition.autoBatch === false ? immediateScheduler : tickScheduler;

    /**
     * Создать функцию отписки для экземпляра
     */
    function createDetachFunction(param: P | undefined): Detach {
        return function detach() {
            const instance = cache.get(param);
            if (!instance) {
                throw new UnexpectedError('Попытка отписаться от экземпляра реакции которого не существует', {
                    cause: null,
                    additional: {
                        instanceKey: safeJsonStringify(param),
                    },
                });
            }

            instance.onDetach();

            if (!instance.hasUsages()) {
                instance.destroy();
                cache.remove(instance.param);

                if (instance.hasActiveRuns()) {
                    orphanedInstances.add(instance);
                }
            }
        };
    }

    function createReturnValue(instance: ReactionInstance<R, P>, detach: Detach): StartReturnValue<R, P> {
        return Object.assign(detach, {
            getInstance() {
                return instance;
            },
            [$INTERNALS]: {
                getUnsubscribes() {
                    return instance.unsubscribes;
                },
                getInstances() {
                    return cache.getAll();
                },
                getOrphanedInstances() {
                    return orphanedInstances;
                },
            },
        });
    }

    function start(): StartReturnValue<R, never>;
    function start(param: P): StartReturnValue<R, P>;
    function start(param?: P): StartReturnValue<R, P> | StartReturnValue<R, never> {
        const detach = createDetachFunction(param);
        let instance = cache.get(param);

        if (instance) {
            instance.onAttach();
            return createReturnValue(instance, detach);
        }

        instance = new ReactionInstance({
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- param имеет тип P. Если у реакции нет параметра, то тут будет undefined и это ок
            param: param as P,
            data: definition.data,
            effect: definition.effect,
            orphanedInstances,
            scheduler,
            reactionError,
            reactionMonitoring,
            instances: cache.getAll(),
        });

        // Если start был вызван в серверном окружении
        if (typeof window === 'undefined') {
            const error = new UnexpectedError('Попытка запуска реакции на сервере', {
                cause: reactionError,
                additional: {
                    instanceKey: safeJsonStringify(param),
                },
            });

            // То в проде логируем ошибку, и не запускаем реакцию
            if (process.env.NODE_ENV === 'production') {
                console.error(error);
                return createReturnValue(instance, detach);
            }

            // В другом случае выбрасываем исключение
            throw error;
        }

        cache.set(
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- param имеет тип P. Если у реакции нет параметра, то тут будет undefined и это ок
            param as P,
            instance
        );

        reactionMonitoring.onInstanceCreate(cache.getAll().size);
        instance.schedule();

        return createReturnValue(instance, detach);
    }

    return {
        start,
        [$INTERNALS]: {
            definition,
            getInstances() {
                return cache.getAll();
            },
        },
    };
}
